
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { Loader2, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const EmailCampaign = () => {
  const [recipientEmail, setRecipientEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [verificationError, setVerificationError] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setVerificationError(false);

    try {
      console.log("Email campaign initiated:", { 
        recipientEmail, 
        subject,
        timestamp: new Date().toISOString()
      });
      
      // Send email using Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('send-email', {
        body: { 
          to: recipientEmail,
          subject: subject,
          text: message,
          html: `<div style="font-family: sans-serif; max-width: 600px; margin: 0 auto;">${message.replace(/\n/g, '<br/>')}</div>`
        }
      });

      if (error) {
        console.error('Error sending email:', error);
        throw error;
      }

      console.log('Email sending result:', data);

      // Check response for sender verification error
      if (data && data.error && data.error.includes && data.error.includes("Sender Identity")) {
        setVerificationError(true);
        toast({
          title: "Sender verification required",
          description: "Your sender email needs to be verified in SendGrid.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Email sent successfully!",
        description: `Your message has been sent to ${recipientEmail}.`,
      });

      // Reset form
      setRecipientEmail("");
      setSubject("");
      setMessage("");
    } catch (error: any) {
      console.error('Error in email campaign:', error);
      
      // Check if it's a sender verification error
      if (error.message && typeof error.message === 'string' && 
         (error.message.includes("Sender Identity") || 
          (error.response && error.response.data && error.response.data.includes("Sender Identity")))) {
        setVerificationError(true);
        toast({
          title: "Sender verification required",
          description: "Your sender email needs to be verified in SendGrid.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to send email. Please try again later.",
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-6">Send Email to Subscriber</h2>
      
      {verificationError && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Sender Email Not Verified</AlertTitle>
          <AlertDescription>
            Your sender email address needs to be verified with SendGrid before sending emails.
            <ol className="list-decimal ml-6 mt-2 space-y-1 text-sm">
              <li>Log in to your SendGrid account</li>
              <li>Go to Settings → Sender Authentication</li>
              <li>Verify your sender email ({process.env.SENDGRID_SENDER_EMAIL || "your configured email"})</li>
              <li>Follow the verification instructions from SendGrid</li>
            </ol>
          </AlertDescription>
        </Alert>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Recipient Email *</Label>
          <Input
            id="email"
            type="email"
            required
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            placeholder="subscriber@example.com"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="subject">Subject *</Label>
          <Input
            id="subject"
            required
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Your email subject"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="message">Message *</Label>
          <Textarea
            id="message"
            required
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Write your email content here..."
            rows={5}
          />
        </div>
        
        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending...
            </>
          ) : (
            "Send Email"
          )}
        </Button>
      </form>
    </div>
  );
};

export default EmailCampaign;
